import { Tabs } from '@design-systems/canvas-ui'
import { PageTabs } from 'components/page-tabs'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { MPMLayout } from 'components/layout'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useLogClick } from 'utils/analytics'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'

const ROUTES = {
    PAYROLL: 'payroll',
    THEFT: 'theft',
    SECURE: 'secure',
}

export const AssetsProtectionPage = () => {
    const { t } = useEssentials()
    const { assetsProtection } = TRANSLATION_KEYS
    const { title, tabs: tabsKeys } = assetsProtection
    const [activeTab, setActiveTab] = useState<string>(ROUTES.PAYROLL)
    const { logClick } = useLogClick('assetsProtection')
    const navigate = useNavigate()
    const { pathname, search } = useLocation()

    const tabsWithTranslations = useMemo(() => {
        const tabs = [
            { key: ROUTES.PAYROLL, label: t(tabsKeys.payroll) },
            { key: ROUTES.THEFT, label: t(tabsKeys.theft) },
            { key: ROUTES.SECURE, label: t(tabsKeys.secure) },
        ]
        return tabs
    }, [t, tabsKeys])

    const handleTabSelect = (activeTab: string) => {
        setActiveTab(activeTab)
        logClick('assets protection tab ' + activeTab)
        void navigate(`/assets-protection/${activeTab}${search}`)
    }

    useEffect(() => {
        const currentPath = pathname.split('/').pop() || ROUTES.PAYROLL
        if (Object.values(ROUTES).includes(currentPath as any)) {
            setActiveTab(currentPath)
        }
    }, [pathname])

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(assetsProtection.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(title)}
                className="hc8-ph-none assets-protection-page"
                hasTabs
                tabs={
                    <PageTabs
                        activeTab={activeTab}
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {tabsWithTranslations.map((tab) => (
                            <Tabs.Item key={tab.key} name={tab.key}>
                                {tab.label}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                }
            >
                <div className="hc8-ph-4x">
                    <Outlet />
                </div>
            </MPMLayout>
        </ErrorBoundary>
    )
}
